import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { GalaxyIcon } from '@/components/icons';
import PublicHeader from '@/components/PublicHeader';
import FeatureCarousel from '@/components/FeatureCarousel';
import ExperiencePulseModal from '@/components/ExperiencePulseModal';
import { 
  BarChart3, 
  Shield, 
  Users, 
  ArrowRight,
  CheckCircle,
  Sparkles,
  Heart,
  Activity,
  Eye,
} from 'lucide-react';

const features = [
  {
    icon: Users,
    title: 'Stakeholder Segmentation',
    description: 'Organize stakeholders into meaningful segments and target questions to specific groups.',
  },
  {
    icon: BarChart3,
    title: 'Sentiment Analytics',
    description: 'Track perception trends over time with powerful dashboards and AI-powered insights.',
  },
  {
    icon: GalaxyIcon,
    title: 'Network Mapping',
    description: 'Visualize stakeholder relationships and influence networks with interactive graphs.',
  },
  {
    icon: Shield,
    title: 'Privacy First',
    description: 'Built-in anonymity protection ensures honest feedback while protecting individual privacy.',
  },
];

const benefits = [
  'Measure stakeholder sentiment across multiple drivers',
  'AI-powered analysis of open-ended feedback',
  'Self-service stakeholder registration',
  'Real-time participation tracking',
  'Export-ready reports and visualizations',
];

export default function LandingPage() {
  const [showExperiencePulse, setShowExperiencePulse] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <PublicHeader />

      {/* Hero Section */}
      <section className="hero-section pt-24 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6">
        <div className="hero-background" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5AA2AE]/10 text-[#5AA2AE] text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Now with AI-powered insights</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-none mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Measure Stakeholder<br />
              <span className="text-[#5AA2AE]">Perception & Sentiment</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-2xl">
              Pulse helps project teams identify, track, and analyze stakeholder sentiment. 
              Get actionable insights to improve engagement and drive project success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90 h-12 px-8 text-base"
                  data-testid="hero-cta-btn"
                >
                  Start 30-Day Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline"
                className="h-12 px-8 text-base border-[#5AA2AE]/30 text-[#5AA2AE] hover:bg-[#5AA2AE]/5 hover:border-[#5AA2AE]/50"
                onClick={() => setShowExperiencePulse(true)}
                data-testid="experience-pulse-btn"
              >
                <Eye className="mr-2 h-5 w-5" />
                Experience Pulse
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Everything you need to understand your stakeholders
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From survey creation to AI analysis, Pulse provides the complete toolkit for stakeholder engagement.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#5AA2AE]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#5AA2AE]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* People-First Banner + Feature Carousel */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Tagline Banner */}
          <div className="relative mb-16 text-center" data-testid="people-first-banner">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#5AA2AE]/5 border border-[#5AA2AE]/15 mb-6">
              <Heart className="w-3.5 h-3.5 text-[#5AA2AE]" />
              <span className="text-xs font-medium text-[#5AA2AE] tracking-wide uppercase">People-first technology</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 max-w-3xl mx-auto leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Use technology to bring people
              <span className="text-[#5AA2AE]"> into </span>
              your project
              <span className="text-slate-300"> — </span>
              <span className="text-slate-400 font-normal italic">not to push them out</span>
            </h2>
            <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
              In the age of AI, technology often finds ways to take people out of the equation.
              Pulse is different — we use it to amplify every voice, because people are still
              the most important part of any project.
            </p>
          </div>

          {/* Feature Carousel */}
          <FeatureCarousel />
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Make better decisions with data-driven insights
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Pulse transforms raw feedback into actionable intelligence, helping you understand 
                what your stakeholders really think and how to improve their experience.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-900 rounded-2xl p-6 lg:p-8 text-white overflow-hidden">
              {/* Key Metrics Row */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[{ v: '78%', l: 'Productivity', d: '+4.2%', up: true }, { v: '92%', l: 'Equipment Avail.', d: '+1.8%', up: true }, { v: '64%', l: 'Resource Mob.', d: '-2.1%', up: false }].map((m, i) => (
                  <div key={i} className="bg-slate-800 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{m.v}</p>
                    <p className="text-[9px] text-slate-400 mt-0.5">{m.l}</p>
                    <span className={`text-[9px] font-medium ${m.up ? 'text-emerald-400' : 'text-rose-400'}`}>{m.d}</span>
                  </div>
                ))}
              </div>

              {/* Trend Graph */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Sentiment Trend — 12 Weeks</span>
                  <span className="text-[10px] text-[#7FCDC1] font-bold">6.6 avg</span>
                </div>
                <svg viewBox="0 0 240 60" className="w-full h-16">
                  {/* Grid lines */}
                  {[0, 20, 40, 60].map(y => <line key={y} x1="0" y1={y} x2="240" y2={y} stroke="#334155" strokeWidth="0.5" />)}
                  {/* Area fill */}
                  <path d="M0,45 L20,40 L40,38 L60,42 L80,35 L100,30 L120,32 L140,28 L160,25 L180,22 L200,20 L220,18 L240,15 L240,60 L0,60 Z" fill="url(#trendGrad)" opacity="0.3" />
                  <defs><linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7FCDC1" /><stop offset="100%" stopColor="transparent" /></linearGradient></defs>
                  {/* Line */}
                  <polyline points="0,45 20,40 40,38 60,42 80,35 100,30 120,32 140,28 160,25 180,22 200,20 220,18 240,15" fill="none" stroke="#7FCDC1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  {/* Latest dot */}
                  <circle cx="240" cy="15" r="3" fill="#7FCDC1" />
                </svg>
              </div>

              {/* AI Insights bullets */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Sparkles className="w-3 h-3 text-amber-400" />
                  <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">AI Insights</span>
                </div>
                <div className="space-y-2">
                  {[
                    { text: 'Productivity scores up 4.2% since safety stand-down — correlates with improved crew morale', color: '#10b981' },
                    { text: 'Equipment availability dipped in Week 8 due to crane maintenance — now recovered to 92%', color: '#3b82f6' },
                    { text: 'Resource mobilisation flagged: 3 subcontractors report delayed site access approvals', color: '#f59e0b' },
                    { text: 'Stakeholder confidence in schedule increased after milestone M4 delivered 2 days early', color: '#7FCDC1' },
                  ].map((insight, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: insight.color }} />
                      <p className="text-[11px] text-slate-300 leading-relaxed">{insight.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#5AA2AE]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to understand your stakeholders better?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Start your free 30-day trial today. No credit card required.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-[#5AA2AE] hover:bg-white/90 h-12 px-8 text-base"
              data-testid="cta-register-btn"
            >
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/projectai-logo.png" alt="ProjectAI" className="h-8 object-contain" />
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Pulse by ProjectAI. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Experience Pulse Modal */}
      <ExperiencePulseModal open={showExperiencePulse} onClose={() => setShowExperiencePulse(false)} />
    </div>
  );
}
